import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { TraditionalLTCQuoteWithROI } from '@client/hooks/useTraditionalLTCQuotesWithROI';
import type { Quote } from '@client/model/quotesModel';
import { makeSelectIsInsuranceQuoteLoading } from '@client/model/selectors';

interface QuoteWithROI extends Quote {
  roi?: number;
  formattedROI?: string;
  projectedCostCoverage?: number;
}

type PolicyQuote = QuoteWithROI | TraditionalLTCQuoteWithROI;

/**
 * Type guard to distinguish life insurance quotes from traditional LTC quotes
 */
function isLifeInsuranceQuote(quote: PolicyQuote): quote is QuoteWithROI {
  return 'insuranceQuoteId' in quote;
}

interface UsePolicyLoadingStateParams {
  quote: PolicyQuote;
  client: Client;
}

interface UsePolicyLoadingStateReturn {
  isLoading: boolean;
  insuranceQuoteId: string;
  fundingSourceId: string | null;
}

/**
 * Custom hook to track the loading state of a policy operation (add/edit/delete)
 *
 * Tracks by:
 * - insuranceQuoteId for add/edit operations
 * - fundingSourceId for delete operations
 */
export function usePolicyLoadingState({
  quote,
  client,
}: UsePolicyLoadingStateParams): UsePolicyLoadingStateReturn {
  const isLifeInsurance = isLifeInsuranceQuote(quote);

  // Get the insuranceQuoteId to check loading state
  const insuranceQuoteId = isLifeInsurance
    ? (quote as QuoteWithROI).insuranceQuoteId
    : (quote as TraditionalLTCQuoteWithROI).traditionalLTCQuoteId;

  // Get the fundingSourceId if this policy is already added (for delete tracking)
  const fundingSourceId = useMemo(() => {
    if (!client?.multipleFundingSources?.ltcPolicy) return null;

    for (const [fsId, policy] of Object.entries(client.multipleFundingSources.ltcPolicy)) {
      if (policy.insuranceQuoteId === insuranceQuoteId) {
        return fsId;
      }
    }
    return null;
  }, [client?.multipleFundingSources?.ltcPolicy, insuranceQuoteId]);

  // Check if this specific quote is currently being operated on
  const isLoading: boolean = useSelector(
    makeSelectIsInsuranceQuoteLoading(insuranceQuoteId, fundingSourceId),
  );

  return {
    isLoading,
    insuranceQuoteId,
    fundingSourceId,
  };
}
